/**
 * Tab-local continuity for an in-progress video application.
 *
 * START.md §20 does not ask for guaranteed recovery — there is no account and no
 * password — but an accidental refresh should not throw the work away either. So we
 * keep three small facts in `sessionStorage` (this tab only, gone when it closes) and
 * re-sync them against the server on the next load.
 *
 * Everything here is defensive: Safari in private mode throws on `sessionStorage`
 * access, and a stored shape from an older build must never crash a new one.
 */

/** Bumped whenever {@link StoredSession} changes shape; a stored record with any other version is discarded. */
const SCHEMA_VERSION = 1;

/** `sessionStorage` key holding the record below. */
const STORAGE_KEY = 'altamont.video-application.v1';

/** The three facts worth carrying across a page refresh. */
export interface StoredSession {
  /** Firestore id of the application being filled in. */
  applicationId: string;
  /** Furthest flow step reached — the `meta.step` of a route in `router/index.ts`. */
  step: number;
  /** Question ids that already have an accepted answer. */
  answeredQuestionIds: string[];
}

/** The on-disk shape: {@link StoredSession} plus the schema version it was written with. */
interface StoredSessionRecord extends StoredSession {
  v: number;
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage;
  } catch {
    // Safari private mode, or storage blocked by policy.
    return null;
  }
}

function isStoredSessionRecord(value: unknown): value is StoredSessionRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return (
    record['v'] === SCHEMA_VERSION &&
    typeof record['applicationId'] === 'string' &&
    record['applicationId'].length > 0 &&
    typeof record['step'] === 'number' &&
    Number.isFinite(record['step']) &&
    Array.isArray(record['answeredQuestionIds']) &&
    record['answeredQuestionIds'].every((id) => typeof id === 'string')
  );
}

/**
 * Read the saved session for this tab, or `null` when there is nothing usable.
 * A corrupt, foreign or out-of-date payload is discarded rather than repaired.
 */
export function loadSession(): StoredSession | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isStoredSessionRecord(parsed)) {
      clearSession();
      return null;
    }
    return {
      applicationId: parsed.applicationId,
      step: parsed.step,
      answeredQuestionIds: [...parsed.answeredQuestionIds],
    };
  } catch {
    clearSession();
    return null;
  }
}

/** Save the session for this tab. Failure is silent: continuity is a convenience, never a requirement. */
export function saveSession(session: StoredSession): void {
  const store = storage();
  if (!store) return;
  try {
    const record: StoredSessionRecord = { v: SCHEMA_VERSION, ...session };
    store.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota exceeded or storage blocked — carry on without continuity.
  }
}

/** Forget this tab's session. Called whenever we start over. */
export function clearSession(): void {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the record simply stays until the tab closes.
  }
}

/**
 * Can this browser record video at all?
 *
 * Both halves are needed: `getUserMedia` for the camera and microphone, `MediaRecorder`
 * for the bytes. Older iOS Safari and most in-app webviews have the first and not the
 * second, and someone deserves to be told that up front instead of at the first question.
 */
export function isRecordingSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const hasCapture = typeof navigator.mediaDevices?.getUserMedia === 'function';
  const hasRecorder = typeof window.MediaRecorder === 'function';
  return hasCapture && hasRecorder;
}
